import {
  Box,
  motion,
  useBoolean,
  useSafeLayoutEffect,
  useSize,
  useTheme,
} from '@hover/blueprint';
import { AnimatePresence } from 'framer-motion';
import React, { useRef, useState } from 'react';

import { MobileHeader } from 'src/modules/homeownerApp/components/MobileHeader';
import type { AiDisplayImageFragment } from 'src/shared/graphql/generated';
import {
  useAiDisplayImagesQuery,
  useAiOriginalImagesQuery,
} from 'src/shared/graphql/generated';
import {
  TranslationNamespace,
  useReactNativeWebView,
  useRefMap,
  useTimeout,
  useTranslate,
} from 'src/shared/hooks';
import { WebViewScope } from 'src/shared/hooks/useReactNativeWebView';
import { Route } from 'src/shared/route';

import { FAVORITES_PAGE_SIZE } from '../../constants';
import { useScrollToImage } from '../../hooks';
import { FullscreenImage, useHideInFullscreen } from '../FullscreenImage';
import { Masonry } from '../Masonry';
import { mobileNavbarHeight } from '../Navbar';
import {
  StackingHeaderProvider,
  StackingHeaderStacker,
} from '../StackingHeader';

import { Card } from './Card';
import { EmptyFavorites } from './EmptyFavorites';
import { SpaceTypeSelector } from './SpaceTypeSelector';

const MotionBox = motion(Box);

const PRELOAD_IMAGE_THRESHOLD = 6;

const isDisplayFavorite = (
  image: AiDisplayImageFragment | null | undefined,
): image is AiDisplayImageFragment => !!image && !!image.favoritedAt;

/**
 * Wait for an element that will have potential layout shift to settle to a
 * stable height
 *
 * @param wait duration to wait between size updates before considering the
 * element "settled". This defaults to `0` as in most cases we just want to wait
 * for the next frame without a size change.
 *
 * @returns a tuple containing the boolean for settled state and a ref to pass
 * to the element to measure
 */
export const useHasSettled = (wait: number = 0) => {
  const sizeRef = useRef<HTMLDivElement>(null);
  const size = useSize(sizeRef);

  const [hasSettled, { on: onSettle }] = useBoolean(false);
  const [timeout] = useTimeout();

  useSafeLayoutEffect(() => {
    timeout(onSettle, wait);
  }, [onSettle, size?.height, timeout, wait]);

  return [hasSettled, sizeRef] as const;
};

export const LoggedInFavorites = () => {
  const t = useTranslate(TranslationNamespace.HOMEOWNER_APP);
  const [isWebView] = useReactNativeWebView(WebViewScope.MOBILE_HOMEOWNER);

  const { space } = useTheme();

  const [spaceTypesList, setSpaceTypesList] = useState<string[]>([]);
  const [currentType, setCurrentType] = useState<string>('All');

  const hideInFullscreen = useHideInFullscreen();

  const { loading: isLoadingOriginalImages } = useAiOriginalImagesQuery({
    ssr: false,
  });

  const { data: displayImagesData, loading: isLoadingDisplayImages } =
    useAiDisplayImagesQuery({
      variables: { favorite: true, perPage: FAVORITES_PAGE_SIZE },
      ssr: false,
    });

  const isLoading = isLoadingOriginalImages || isLoadingDisplayImages;

  const { getElement, getRef } = useRefMap<number>();

  const [scrollToId, clearScrollTo] = useScrollToImage();
  const [hasSettled, settleRef] = useHasSettled();

  useSafeLayoutEffect(() => {
    if (!scrollToId || !hasSettled) return;

    const element = getElement(scrollToId);
    if (!element) return;

    element.scrollIntoView({ block: 'center' });
    clearScrollTo();
  }, [clearScrollTo, getElement, hasSettled, scrollToId]);

  const isMultipleSpaceType = spaceTypesList.length > 1;

  const favoriteDisplayData = displayImagesData?.aiDisplayImages?.results
    .filter(isDisplayFavorite)
    .sort(({ favoritedAt: favoritedAtA }, { favoritedAt: favoritedAtB }) => {
      if (!favoritedAtA || !favoritedAtB) return 0;
      const secondsA = new Date(favoritedAtA).getTime();
      const secondsB = new Date(favoritedAtB).getTime();

      return secondsB - secondsA;
    });

  if (!isLoading && !favoriteDisplayData?.length) return <EmptyFavorites />;

  favoriteDisplayData?.forEach(fav => {
    if (
      fav.aiGeneratedImage.spaceType &&
      !spaceTypesList.includes(fav.aiGeneratedImage.spaceType)
    ) {
      setSpaceTypesList([...spaceTypesList, fav.aiGeneratedImage.spaceType]);
    }
  });

  const filteredDisplayFavorites =
    currentType === 'All'
      ? favoriteDisplayData
      : favoriteDisplayData?.filter(
          displayImage =>
            displayImage.aiGeneratedImage.spaceType === currentType,
        );

  const filteredFavorites = filteredDisplayFavorites?.map(
    displayImage => displayImage.aiGeneratedImage,
  );

  const willScroll = !!scrollToId;
  const showSubHeading = !!filteredFavorites?.length;
  const subHeadingText = t('favorites.numFavorites', {
    count: filteredFavorites?.length,
  });

  return (
    <StackingHeaderProvider
      height={{
        desktop: { expanded: 130, collapsed: 60 },
        base: { expanded: 80, collapsed: 0 },
      }}
    >
      {!isWebView && (
        <MobileHeader
          headingText={t('favorites.saved')}
          isLoading={isLoading}
          showHeaderText={false}
          showProfileIcon
          showSubHeading={showSubHeading}
          subHeadingText={subHeadingText}
        />
      )}

      <MotionBox
        alignItems={{ tablet: 'center' }}
        animate={hasSettled ? { opacity: 1 } : undefined}
        flexDir="column"
        initial={willScroll ? { opacity: 0 } : false}
        pb={{ base: `${mobileNavbarHeight}px`, tablet: 0 }}
        ref={settleRef}
        width="100%"
      >
        <StackingHeaderStacker
          options={{
            expanded: '75px',
            collapsed: '64px',
          }}
          width="100%"
        >
          <SpaceTypeSelector
            currentType={currentType}
            isLoading={isLoading}
            onTypeChange={(type: string) => setCurrentType(type)}
            // The `base` value here should match the padding of the page
            // container's mobile layout so the scroll contents of the
            // `Selector` are flush with the edges of the page container.
            overflowDistanceX={{ base: space[500], tablet: space[200] }}
            overflowDistanceY={space[200]}
            placeholderCount={5}
            sx={hideInFullscreen}
            types={spaceTypesList}
          />
        </StackingHeaderStacker>
        <FullscreenImage
          images={filteredDisplayFavorites}
          path={Route.HOMEOWNER_APP_SAVED_DISPLAY_FULLSCREEN.path}
          returnPath={Route.HOMEOWNER_APP_SAVED.path}
        >
          <Masonry
            columns={{
              base: 1,
              mobile: isMultipleSpaceType ? 2 : 1,
              tablet: 3,
            }}
            fallbackRowHeight={{ base: 250, width250: 300, tablet: 400 }}
            gap={{ base: 300, width250: 400, tablet: 500 }}
            isLoading={isLoading}
            // Re-render the entire list when filtering. This allows us to use
            // `AnimatePresence` for removing an individual item without it running
            // when the filter changes.
            key={currentType}
            preloadImages={
              // Only wait to preload images when we first show them all. Since
              // we're only filtering on the client for now, images are already
              // loaded when changing the filter. We probably want to remove this
              // if and when we start filtering on the server.
              currentType === 'All'
                ? filteredFavorites
                    ?.slice(0, PRELOAD_IMAGE_THRESHOLD)
                    .map(i => i.generatedImagePath)
                : undefined
            }
            px={{ base: 500, tablet: 800 }}
            py={{ base: 400, tablet: 600 }}
            role="list"
            waitForImages={!willScroll}
            width="100%"
          >
            <AnimatePresence>
              {filteredDisplayFavorites?.map(displayImage => (
                <Card
                  exit={{ opacity: 0 }}
                  image={displayImage}
                  initial={false}
                  key={displayImage.id}
                  ref={getRef(displayImage.aiGeneratedImage.id)}
                  transition={{ duration: 0.75 }}
                />
              ))}
            </AnimatePresence>
          </Masonry>
        </FullscreenImage>
      </MotionBox>
    </StackingHeaderProvider>
  );
};
